"use client"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"

interface TickerData {
  ticker: string
  annualizedStandardDeviation: number
  halfLife: number
  sic: string
  models: {
    model: string
    blockage: number
    dlom: number
  }[]
}

interface TickerResultsProps {
  data: TickerData | null
  isLoading: boolean
}

export function TickerResults({ data, isLoading }: TickerResultsProps) {
  if (isLoading) {
    return (
      <Card className="min-h-[200px]">
        <CardContent className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Searching tickers...</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!data) {
    return (
      <Card className="min-h-[300px] border-2 border-dashed border-border">
        <CardContent className="flex items-center justify-center py-12">
          <div className="text-center max-w-md">
            <h3 className="text-lg font-semibold text-foreground mb-2">No Data Available</h3>
            <p className="text-muted-foreground mb-4">
              Enter search criteria above to retrieve ticker data and discount calculations.
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* General Overview Table */}
      <Card>
        <CardHeader>
          <CardTitle className="bg-muted text-muted-foreground p-3 rounded-t-lg -mt-6 -mx-6 mb-0">
            Original Description Details
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-bold text-foreground">Ticker</TableHead>
                <TableHead className="font-bold text-foreground">Annualized Standard Deviation</TableHead>
                <TableHead className="font-bold text-foreground">Half Life</TableHead>
                <TableHead className="font-bold text-foreground">Sic</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-medium">{data.ticker}</TableCell>
                <TableCell>{data.annualizedStandardDeviation.toFixed(2)}%</TableCell>
                <TableCell>{data.halfLife.toFixed(2)}</TableCell>
                <TableCell>{data.sic}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Blockage and Discount Table */}
      <Card>
        <CardHeader>
          <CardTitle className="bg-muted text-muted-foreground p-3 rounded-t-lg -mt-6 -mx-6 mb-0">
            Discount ({data.ticker})
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-bold text-foreground">Model</TableHead>
                <TableHead className="font-bold text-foreground">Blockage</TableHead>
                <TableHead className="font-bold text-foreground">Dlom</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.models.map((model, index) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">{model.model}</TableCell>
                  <TableCell>{model.blockage.toFixed(2)}%</TableCell>
                  <TableCell>{model.dlom.toFixed(2)}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}
